import React, { createContext, useContext, useMemo, useReducer, useState } from 'react';
import { AI_API_URL } from '../config';
import { getStorage, setStorage } from '../utils/storegeUtils';
const PERSIST_SERVER_CONF = 'config_mint_server_conf';
const PERSIST_ROLE = 'config_mint_role';
const PERSIST_LANG_MINT = 'i18nextLng_mint';
const MintConfigContext = createContext();

const actions = {
  CHANGE_LANG_MINT: 'CHANGE_LANG_MINT',
  CHANGE_ROLE: 'CHANGE_ROLE',
  CHANGE_SERVER_CONF: 'CHANGE_DOMAIN',
  CHANGE_LESSON_FROM_API: 'CHANGE_LESSON_FROM_API',
};

const initialState = {
  lang: getStorage(PERSIST_LANG_MINT) || 'en',
  role: getStorage(PERSIST_ROLE) || 'Teacher',
  serverApi: JSON.parse(
    getStorage(PERSIST_SERVER_CONF) ||
      '{"api":"https://edelvivesdigital-dev.oneclickaws.tk/api","lessons":["e4ebee80-3f0f-11ed-8947-051205ae9a08"],"token":""}'
  ),
  aiApi: AI_API_URL,
  lessonFromApi: null,
  lessonFileDownloadURL: null,
};

const LANGS_AVAILABLE = [
  { name: 'English', code: 'en' },
  { name: 'Español', code: 'es' },
  { name: 'Catalán', code: 'ca' },
  { name: 'Euskera', code: 'eu' },
  { name: 'Gallego', code: 'gl' },
  { name: 'Valenciano', code: 'vl' },
];

const DOMAINS = [
  { name: 'Development', url: 'https://edelvivesdigital-dev.oneclickaws.tk/api' },
  { name: 'QA', url: 'https://edelvivesdigital-qa.oneclickaws.tk/api' },
  { name: 'Production', url: 'https://edelvivesdigitalplus.com/api' },
];

function mintConfigReducer(state, action) {
  switch (action.type) {
    case actions.CHANGE_LANG_MINT: {
      setStorage(PERSIST_LANG_MINT, action.payload.lang);
      return { ...state, lang: action.payload.lang };
    }
    case actions.CHANGE_SERVER_CONF: {
      try {
        setStorage(PERSIST_SERVER_CONF, JSON.stringify(action.payload.serverApi));
        return { ...state, serverApi: action.payload.serverApi };
      } catch (err) {
        console.log(err);
      }
      break;
    }
    case actions.CHANGE_ROLE: {
      setStorage(PERSIST_ROLE, action.payload.role);
      return { ...state, role: action.payload.role };
    }
    case actions.CHANGE_LESSON_FROM_API: {
      return { ...state, lessonFromApi: action.payload.lesson };
    }
    case actions.CHANGE_LESSON_FILE_DOWNLOAD_URL: {
      return { ...state, lessonFileDownloadURL: action.payload.url };
    }
    default: {
      throw new Error(`Unsupported action type: ${action.type}`);
    }
  }
}

function useMintConfig() {
  const context = useContext(MintConfigContext);
  if (!context) {
    throw new Error(`useMintConfig must be used within a MintConfigProvider`);
  }
  const [state, dispatch] = context;
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState('');

  const changeLang = (lang) => dispatch({ type: actions.CHANGE_LANG_MINT, payload: { lang } });
  const setServerApi = (conf) => dispatch({ type: actions.CHANGE_SERVER_CONF, payload: { serverApi: conf } });
  const setRole = (role) => dispatch({ type: actions.CHANGE_ROLE, payload: { role } });
  const setLessonFromApi = (lesson) => dispatch({ type: actions.CHANGE_LESSON_FROM_API, payload: { lesson } });
  const setLessonFileDownloadURL = (url) =>
    dispatch({ type: actions.CHANGE_LESSON_FILE_DOWNLOAD_URL, payload: { url } });

  async function handleFileUpload(file, isGeneratingZip) {
    if (!file || file.type !== 'application/pdf') {
      setUploadError('Please select a valid PDF file');
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadProgress('Preparing file...');

    try {
      const formData = new FormData();
      formData.append('file', file);

      setUploadProgress('Processing PDF...');

      const response = await fetch(`${state.aiApi}/pdf/${isGeneratingZip ? 'tomint-zip' : 'tomint'}`, {
        method: 'POST',
        body: formData,
      });

      setUploadProgress('Analyzing content...');
      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Error processing file');
      }

      if (!data?.mint) {
        throw new Error('Invalid response format');
      }

      const post = data.mint.map((item, index) => ({
        id: index,
        items: item,
      }));

      setUploadProgress('File processed successfully!');
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress('');
        setLessonFromApi({ id: '1', items: post });
        setLessonFileDownloadURL(data.fileUrl);
      }, 1000);
    } catch (error) {
      const errorMessage = error.message.includes('Error converting')
        ? error.message
        : `Error processing file: ${error.message}`;

      setUploadError(errorMessage);
      setIsUploading(false);
      setUploadProgress('');
    }
  }

  return {
    mintConfig: state,
    langs: LANGS_AVAILABLE,
    domains: DOMAINS,
    dispatch,
    changeLang,
    setServerApi,
    setRole,
    handleFileUpload,
    isUploading,
    uploadError,
    uploadProgress,
  };
}

function MintConfigProvider(props) {
  const [state, dispatch] = useReducer(mintConfigReducer, initialState);
  const value = useMemo(() => [state, dispatch], [state]);
  return <MintConfigContext.Provider value={value} {...props} />;
}

export { MintConfigProvider, useMintConfig };
